import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  inject,
  Input,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import {
  DASHBOARD_USER_TYPE_LABELS,
  DASHBOARD_USER_TYPES,
  DashboardUserType,
  User,
  UserState,
} from '@app/state';
import { AppRoutes, NavLinks } from '@app/core/routing';
import { ButtonComponent, IconComponent } from '@app/ui';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { HeaderUserProfileComponent } from '../header-user-profile/header-user-profile.component';
import { HeaderActionsService } from '../header-actions.service';

export type HeaderAccountMenuMode = 'dropdown' | 'drawer';
export type HeaderAccountMenuTone = 'default' | 'overlay';

const ROW =
  'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors';
const ROW_HOVER = ' hover:[background:var(--border-color-contrast)]';
const DIVIDER = 'mx-3 my-2 border-t border-base-300';

@Component({
  selector: 'gb-header-account-menu',
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonComponent, IconComponent, HeaderUserProfileComponent],
  templateUrl: './header-account-menu.component.html',
  styleUrls: ['./header-account-menu.component.scss'],
})
export class HeaderAccountMenuComponent {
  protected readonly AppRoutes = AppRoutes;
  protected readonly NavLinks = NavLinks;
  protected readonly rowClass = ROW;
  protected readonly hoverClass = ROW_HOVER;
  protected readonly dividerClass = DIVIDER;

  /** `dropdown` for desktop navbar; `drawer` for mobile side panels. */
  @Input() mode: HeaderAccountMenuMode = 'dropdown';
  /** `overlay` softens chrome for transparent navs over dark imagery. */
  @Input() tone: HeaderAccountMenuTone = 'default';
  /** Full-width Sign in / Log in control (drawer / stacked layouts). */
  @Input() blockSignIn = false;
  /** Label for the unauthenticated CTA. */
  @Input() signInLabel = 'Sign in';
  /**
   * When `mode` is `drawer`, whether to render Sign in for guests.
   * Parents that place Sign in in a CTA footer should leave this false.
   */
  @Input() showUnauthenticatedCta = true;
  /**
   * When false, omit Discover Campaigns (parent already renders it above).
   */
  @Input() showDiscoverCampaigns = true;
  /** Charity dashboard URL opened in a new tab when viewing as charity. */
  @Input() dashboardUrl = '/dashboard';

  /** Emitted when a menu item navigates or logs out (so parents can close drawers). */
  @Output() menuAction = new EventEmitter<void>();

  /** Drawer mode fills the parent panel width. */
  @HostBinding('class.block')
  @HostBinding('class.w-full')
  get hostFullWidth(): boolean {
    return this.mode === 'drawer';
  }

  private readonly store = inject(Store);

  user$: Observable<User> = this.store.select(UserState.user);
  dashboardUserType$: Observable<DashboardUserType> = this.store.select(
    UserState.effectiveDashboardUserType
  );

  readonly dashboardUserTypes = DASHBOARD_USER_TYPES;
  readonly dashboardUserTypeLabels = DASHBOARD_USER_TYPE_LABELS;

  isBrowser = false;
  isUserLoading = false;

  private headerActions = inject(HeaderActionsService);

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.headerActions.populateUserIfNeeded(this.isBrowser, (loading) => {
      this.isUserLoading = loading;
    });
  }

  get isAuthenticated(): boolean {
    return this.isBrowser && this.headerActions.isAuthenticated;
  }

  get showSignIn(): boolean {
    return this.isBrowser && !this.isAuthenticated;
  }

  setDashboardUserType(type: DashboardUserType): void {
    this.menuAction.emit();
    this.headerActions.setDashboardUserType(type);
  }

  goToLogin(): void {
    this.menuAction.emit();
    this.headerActions.goToLogin();
  }

  onNavigate(): void {
    this.menuAction.emit();
  }

  logout(): void {
    this.menuAction.emit();
    this.headerActions.logout();
  }
}
